import React, { Component } from 'react'
import {
    Card, Table, Form, Input, Button, Select, DatePicker, Tag, Space
} from 'antd'
import { getApiCredentialAuditLogs, getUserListForDropbox } from '../../request'
import moment from 'moment'

/**
 * API Key 调用审计日志
 * onlyMine: 为 true 时仅展示当前用户日志，隐藏用户筛选器（个人中心场景）
 * fixedUserId: 锁定用户ID（onlyMine=true 时由父组件传入）
 *
 * 后端接口约定：
 *   - userId 为空  → 查询全部（管理员场景，后端需校验调用方具有管理员权限）
 *   - userId 有值  → 按该 userId 过滤（后端需校验调用方是该用户本人或管理员）
 */
export default class ApiKeyAuditLog extends Component {
    formRef = React.createRef()

    state = {
        dataSource: [],
        userList: [],
        columns: [],
        loading: false,
        total: 0,
        formParam: {
            currentPage: 1,
            pageSize: 20,
        },
    }

    componentDidMount() {
        const { fixedUserId } = this.props
        // columns 依赖 onlyMine prop，初始化一次存入 state
        this.setState({ columns: this.buildColumns() })
        if (fixedUserId) {
            this.setState(
                { formParam: { ...this.state.formParam, userId: fixedUserId } },
                () => this.initList()
            )
        } else {
            this.initList()
            this.initUserList()
        }
    }

    initList = () => {
        this.setState({ loading: true })
        getApiCredentialAuditLogs(this.state.formParam)
            .then(res => {
                this.setState({
                    loading: false,
                    dataSource: Array.isArray(res.list) ? res.list : [],
                    total: res.total || 0,
                })
            })
            .catch(() => this.setState({ loading: false }))
    }

    initUserList = () => {
        getUserListForDropbox({ type: 0 }).then(res => {
            this.setState({ userList: res || [] })
        })
    }

    handleSearch = () => {
        this.setState(
            { formParam: { ...this.state.formParam, currentPage: 1 } },
            this.initList
        )
    }

    handleReset = () => {
        this.formRef.current.resetFields()
        const { fixedUserId } = this.props
        this.setState(
            {
                formParam: {
                    currentPage: 1,
                    pageSize: 20,
                    ...(fixedUserId ? { userId: fixedUserId } : {}),
                },
            },
            this.initList
        )
    }

    handleValuesChange = (changed) => {
        const param = { ...this.state.formParam }
        if ('requestRangeTime' in changed) {
            const range = changed.requestRangeTime
            param.start = range ? moment(range[0]).format('YYYY-MM-DD HH:mm:ss') : null
            param.end = range ? moment(range[1]).format('YYYY-MM-DD HH:mm:ss') : null
        } else {
            const key = Object.keys(changed)[0]
            param[key] = changed[key] ?? null
        }
        this.setState({ formParam: param })
    }

    buildColumns = () => {
        const { onlyMine } = this.props
        const cols = [
            {
                title: '序号',
                key: 'index',
                width: 60,
                fixed: 'left',
                render: (_, __, index) => index + 1,
            },
            {
                title: '请求时间',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '-',
            },
            {
                title: '接口路径',
                dataIndex: 'endpoint',
                key: 'endpoint',
                width: 220,
            },
            {
                title: '方法',
                dataIndex: 'method',
                key: 'method',
                width: 80,
                render: (m) => m ? <Tag color={m === 'GET' ? 'blue' : m === 'POST' ? 'green' : 'orange'}>{m.toUpperCase()}</Tag> : '-',
            },
            {
                title: 'Scope',
                dataIndex: 'scopeUsed',
                key: 'scopeUsed',
                width: 150,
                render: (s) => s ? <Tag color="purple">{s}</Tag> : '-',
            },
            {
                title: '状态码',
                dataIndex: 'statusCode',
                key: 'statusCode',
                width: 90,
                render: (code) => {
                    const color = code >= 200 && code < 300 ? 'success' : code >= 400 ? 'error' : 'warning'
                    return <Tag color={color}>{code}</Tag>
                },
            },
            {
                title: '耗时(ms)',
                dataIndex: 'latencyMs',
                key: 'latencyMs',
                width: 100,
            },
            {
                title: '客户端IP',
                dataIndex: 'ip',
                key: 'ip',
                width: 140,
            },
            {
                title: '错误码',
                dataIndex: 'errorCode',
                key: 'errorCode',
                width: 120,
                render: (e) => e || '-',
            },
        ]
        if (!onlyMine) {
            cols.splice(1, 0, {
                title: '用户',
                dataIndex: 'userNick',
                key: 'userNick',
                width: 100,
            })
            cols.splice(2, 0, {
                title: 'Key前缀',
                dataIndex: 'keyPrefix',
                key: 'keyPrefix',
                width: 140,
            })
        } else {
            cols.splice(1, 0, {
                title: 'Key名称',
                dataIndex: 'credentialName',
                key: 'credentialName',
                width: 140,
            })
        }
        return cols
    }

    render() {
        const { onlyMine } = this.props
        const { dataSource, columns, loading, total, userList } = this.state

        return (
            <div>
                <Card title="API 调用日志" bordered={false}>
                    <Form
                        ref={this.formRef}
                        layout="inline"
                        onValuesChange={this.handleValuesChange}
                        style={{ marginBottom: 16 }}
                    >
                        {!onlyMine && (
                            <Form.Item name="userId">
                                <Select
                                    style={{ width: 200 }}
                                    showSearch
                                    allowClear
                                    placeholder="按用户筛选"
                                    optionFilterProp="children"
                                    fieldNames={{ label: 'USER_NICK', value: 'USER_ID' }}
                                    filterOption={(input, option) =>
                                        ((option?.USER_NICK ?? '') + (option?.pinyin ?? '')).toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={userList}
                                />
                            </Form.Item>
                        )}
                        <Form.Item name="endpoint">
                            <Input placeholder="接口路径" style={{ width: 200 }} allowClear />
                        </Form.Item>
                        <Form.Item name="method">
                            <Select
                                style={{ width: 120 }}
                                allowClear
                                placeholder="请求方法"
                                options={[
                                    { label: 'GET', value: 'GET' },
                                    { label: 'POST', value: 'POST' },
                                    { label: 'PUT', value: 'PUT' },
                                    { label: 'DELETE', value: 'DELETE' },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="statusCode">
                            <Input placeholder="状态码" style={{ width: 100 }} allowClear />
                        </Form.Item>
                        <Form.Item name="requestRangeTime">
                            <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm:ss" />
                        </Form.Item>
                        <Form.Item>
                            <Space>
                                <Button type="primary" onClick={this.handleSearch}>查询</Button>
                                <Button onClick={this.handleReset}>重置</Button>
                            </Space>
                        </Form.Item>
                    </Form>

                    <Table
                        bordered
                        size="small"
                        rowKey="id"
                        dataSource={dataSource}
                        columns={columns}
                        loading={loading}
                        scroll={{ x: onlyMine ? 1000 : 1200 }}
                        pagination={{
                            size: 'small',
                            current: this.state.formParam.currentPage,
                            pageSize: this.state.formParam.pageSize,
                            total,
                            showSizeChanger: true,
                            onChange: (page, pageSize) => {
                                this.setState(
                                    { formParam: { ...this.state.formParam, currentPage: page, pageSize } },
                                    this.initList
                                )
                            },
                        }}
                    />
                </Card>
            </div>
        )
    }
}
